import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useAuth } from "../../../context/AuthContext";
import { useAlert } from "../../../context/AlertContext";
import { useLoading } from "../../../context/LoadingContext";
import { AddSubIcon } from "../../../data/iconImage";
import { autoDisplayValue } from "../../../commons/FormatValue";
import { getServiceRequest } from "../../../commons/ServiceRequest";
import { FloatingControlComponent } from "../../FormComponent";
import {
  filteredItemByArray,
  filteredItemBySearch,
  findItemInArray
} from "../../../utils/ManageArray";
import TableForm from "../../TableForm";
import SearchFilter from "../../SearchFilter";
import ConfirmModal from "../../ConfirmModal";
import CardDisplayComponent from "../../CardDisplayComponent";
import CusBranchsService from "../../../services/cusbranch.service";
import ProdSetMcsService from "../../../services/prodsetmc.service";
import CustContractItemsService from "../../../services/custcontractitem.service";

const BranchSerialNumberForm = ({ editData, onSubmitCallback }) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const { setLoading } = useLoading();
  const [showModal, setShowModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [clearSearchBtn, setClearSearchBtn] = useState(false);
  const [searchValue, setSearchValue] = useState([]);
  const [prodMcs, setProdMcs] = useState([]);
  const [cusBranch, setCusBranch] = useState([]);
  const [contract, setContract] = useState([]);
  const defaultData = {
    Item_Id: "",
    Cus_Id: "",
    Mc_Location: "",
    Mc_No: "",
    Cust_NumBranch: "",
    Mc_Type_Id: "",
    Mc_Running: "",
    Seq_Work: "",
    Com_Code: user.Com_Code
  };
  const [data, setData] = useState({ Array: [] });
  const rowData = data?.Array?.[0];
  const defaultSearch = [
    {
      name: "Contract_No",
      label: "เลขที่สัญญา",
      findValue: { data: cusBranch, key: "Cus_Id", value: rowData?.Cus_Id }
    },
    {
      name: "Customer.Cus_Code",
      label: "รหัสสาขา",
      findValue: { data: cusBranch, key: "Cus_Id", value: rowData?.Cus_Id }
    },
    {
      name: "Customer.Cus_NumBranch",
      label: "เลขที่สาขา",
      size: "1_5",
      findValue: { data: cusBranch, key: "Cus_Id", value: rowData?.Cus_Id }
    },
    {
      name: "Customer.Cus_Name",
      label: "ชื่อสาขา",
      size: "4_5",
      findValue: { data: cusBranch, key: "Cus_Id", value: rowData?.Cus_Id }
    }
  ];
  const [searchInput, setSearchInput] = useState(defaultSearch);
  const columns = [
    {
      name: "Item.Item_Code",
      label: "รหัสสินค้า",
      readOnly: true,
      findValue: { data: contract, key: "Item_Id", value: "Item_Id" }
    },
    {
      name: "Item.Item_Name_TH",
      label: "รายละเอียดสินค้า",
      readOnly: true,
      findValue: { data: contract, key: "Item_Id", value: "Item_Id" }
    },
    {
      name: "Item.Serial_No",
      label: "หมายเลขเครื่อง (Serial No.)",
      readOnly: true,
      findValue: { data: contract, key: "Item_Id", value: "Item_Id" }
    },
    { name: "Mc_No", label: "หมายเลขเครื่องประจำสาขา", readOnly: true },
    { name: "Seq_Work", label: "Seq. Work" },
    { name: "Mc_Location", label: "จุดติดตั้งเครื่อง" }
  ];

  const getCustContractItems = (Contract_No) => {
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: CustContractItemsService.getAllCustContractItems(
        `?keyword=${Contract_No}`
      ),
      callback: ({ items }) => {
        if (items?.length === 0) {
          showAlert({
            error: "ข้อผิดพลาด",
            message: "กรุณาผูกสัญญากับสินค้าก่อน",
            type: "danger"
          });
        }
        setContract(items);
      }
    });
  };

  const handleSearchFilter = (search) => {
    const updatedData = filteredItemBySearch(cusBranch, search)
      .flatMap(({ Cus_Id, CustContract }) =>
        CustContract?.CustContractItem.map(({ Item_Id }) => ({
          ...defaultData,
          Cus_Id,
          Item_Id
        }))
      )
      .filter(
        ({ Cus_Id, Item_Id }) =>
          !prodMcs.some(
            (prod) => prod.Cus_Id === Cus_Id && prod.Item_Id === Item_Id
          )
      );
    if (updatedData?.length === 0) {
      showAlert({
        error: "ข้อผิดพลาด",
        message:
          "รหัสสาขานี้ถูกสร้างหมายเลขเครื่องประจำสาขาแล้ว กรุณาเลือกรหัสสาขาใหม่",
        type: "danger"
      });
      return setData({ Array: [] });
    }
    setData({ Array: updatedData });
    setClearSearchBtn(updatedData === 0);
  };

  const checkValidity = () => {
    if (data?.Array.some(({ Mc_No }) => Mc_No === "")) {
      showAlert({
        error: "ข้อผิดพลาด",
        message: "กรุณาสร้างหมายเลขเครื่องประจำสาขาให้ครบถ้วน",
        type: "danger"
      });
      return false;
    }
    return true;
  };

  const handleValidated = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity() || !checkValidity()) {
      event.stopPropagation();
      setValidated(true);
    } else {
      setShowModal(true);
    }
  };

  const handleSubmit = (event, { dataCallback, showCallback }) => {
    event.preventDefault();
    setShowModal(showCallback);

    if (dataCallback) {
      if (!editData) onSubmitCallback(dataCallback);
      else onSubmitCallback(dataCallback.Array[0]);
    }
  };

  useEffect(() => {
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: ProdSetMcsService.getAllProdSetMcs(),
      callback: (res) => setProdMcs(res)
    });
    if (cusBranch?.length === 0) {
      getServiceRequest({
        showAlert,
        setLoading,
        serviceRequest: CusBranchsService.getAllCusBranchs(),
        callback: (items) => {
          setCusBranch(items);
          if (editData) {
            getCustContractItems(
              findItemInArray(items, "Cus_Id", editData?.Cus_Id)?.Contract_No
            );
          }
        }
      });
    }

    if (searchValue?.length > 0 && contract?.length > 0) {
      handleSearchFilter(searchValue, contract);
    }

    if (state?.contract?.length > 0) setContract(state?.contract);

    if (state?.data && state?.cusBranch) {
      const updatedSearch = searchInput.map((search) => ({
        ...search,
        value: autoDisplayValue({
          name: search?.name,
          findValue: {
            data: state?.cusBranch,
            key: "Cus_Id",
            value: state?.row?.Cus_Id
          }
        })
      }));
      setSearchInput(updatedSearch);
      setData({ Array: state?.data });
    }

    if (editData) {
      setData({
        Array: [
          {
            Item_Id: editData.Item_Id,
            Cus_Id: editData.Cus_Id,
            Mc_Location: editData.Mc_Location,
            Mc_No: editData.Mc_No,
            Cust_NumBranch: editData.Cust_NumBranch,
            Mc_Type_Id: editData.Mc_Type_Id,
            Mc_Running: editData.Mc_Running,
            Seq_Work: editData.Seq_Work,
            Com_Code: editData.Com_Code
          }
        ]
      });
    }
  }, [editData, state?.data, searchValue, contract]);

  return (
    <>
      <CardDisplayComponent
        body={{
          as: Row,
          element: (
            <>
              {!editData ? (
                <SearchFilter
                  data={cusBranch}
                  inputField={searchInput}
                  clearBtnOnClick={clearSearchBtn}
                  fieldClassName={{
                    inputRow: "justify-content-end align-items-center",
                    actionButton: "w-auto mt-3"
                  }}
                  searchCallback={(search) => {
                    const findSearch = findItemInArray(
                      search,
                      "name",
                      "Contract_No"
                    );
                    if (findSearch?.value) {
                      setSearchValue(search);
                      getCustContractItems(findSearch?.value);
                    } else setData({ Array: [] });
                  }}
                />
              ) : (
                defaultSearch.map(({ name, label, size, findValue }, idx) => (
                  <Col key={idx} sm={size || 3}>
                    <FloatingControlComponent
                      disabled
                      type="text"
                      name={name}
                      label={label}
                      value={autoDisplayValue({ name, findValue })}
                    />
                  </Col>
                ))
              )}
            </>
          )
        }}
      />

      <Form noValidate validated={validated} onSubmit={handleValidated}>
        <CardDisplayComponent
          body={{
            as: Row,
            element: (
              <TableForm
                data={data.Array}
                columns={columns}
                editForm={!!editData}
                defaultData={defaultData}
                onChangeCallback={(newData) => setData({ Array: newData })}
                linkBtn={[
                  {
                    state: { ...state, contract, cusBranch },
                    button: {
                      color: "light",
                      className:
                        "h-100 p-2 bg-light bg-opacity-0 border-light border-opacity-0 rounded-circle "
                    },
                    icon: { src: AddSubIcon, alt: "AddSub Icon" }
                  }
                ]}
              />
            )
          }}
          footer={{
            className: "text-end",
            element: (
              <>
                <Button
                  type="submit"
                  variant="success"
                  className="me-2 px-5 py-2 text-dark rounded-pill"
                >
                  {editData ? "บันทึกการแก้ไข" : "บันทึกข้อมูล"}
                </Button>
                <ConfirmModal
                  show={showModal}
                  data={data}
                  content={editData ? "edit" : "save"}
                  onSubmitCallback={handleSubmit}
                />

                <Button
                  variant="outline-success"
                  className="px-5 py-2 text-dark border-dark border-opacity-50 rounded-pill"
                  onClick={() => navigate("/PM_work/bind", { state })}
                >
                  ยกเลิก
                </Button>
              </>
            )
          }}
        />
      </Form>
    </>
  );
};

BranchSerialNumberForm.propTypes = {
  editData: PropTypes.object,
  onSubmitCallback: PropTypes.func
};

export default BranchSerialNumberForm;
